/**
 * Owning vertical: ui (INC-0609 risk view, Item 151). The INC-0807 resume gate as data: the
 * guard the command dialog enforces (cooldown, written review, out-of-band code) and the
 * blockers that stand before it can open, derived from the server's routing facts so the risk
 * view's Resume and the routing page's Resume refuse for the same reasons.
 */
import { ContractViolation, type RoutingFacts } from "../contracts/index.ts";
import { cooldownEnd } from "../views/routing/model.ts";
import type { Mode } from "../contracts/envelope.ts";
import { codeGuardOf, type RoutingGuard } from "./command-dialog.ts";
import type { ServerClock } from "./time.ts";

/** The result of the gate: whether the launcher may open, why not, and the dialog's guard. */
export interface ResumeGate {
  /** The launcher is applicable (entries paused and the account halted). */
  readonly ready: boolean;
  /** Said beside the control when it is not ready; `undefined` once it is. */
  readonly blocker: string | undefined;
  /** The guard the dialog enforces before any step-up or POST; absent while a blocker stands. */
  readonly guard: RoutingGuard | undefined;
}

/**
 * The one row of a routing-facts envelope, checked as the routing page checks it.
 *
 * Parameters
 * ----------
 * envelope : object
 *     The decoded envelope's account, snapshot time and rows.
 * account : string
 *     The selected account.
 *
 * Returns
 * -------
 * RoutingFacts
 *     The single row.
 *
 * Raises
 * ------
 * ContractViolation
 *     When the rows are not exactly one for this account, or the halt post-dates the snapshot.
 */
export function factsRowOf(
  envelope: {
    readonly accountId: string | null;
    readonly snapshotTime: string;
    readonly page: { readonly rows: readonly RoutingFacts[] };
  },
  account: string,
): RoutingFacts {
  const [row] = envelope.page.rows;
  if (envelope.accountId !== account || row === undefined || envelope.page.rows.length !== 1) {
    throw new ContractViolation("routing_facts", "one row for the selected account");
  }
  if (row.halt !== null && row.halt.haltedSince > envelope.snapshotTime) {
    throw new ContractViolation("routing_facts.halt", "a halt that began before the snapshot");
  }
  return row;
}

/**
 * Whether Resume may open now, and the guard it carries.
 *
 * Fails closed: no facts, no halt, no confirmation record or entries not paused is a blocker and
 * no guard, so nothing opens and nothing is sent.
 *
 * Parameters
 * ----------
 * facts : RoutingFacts or null
 *     The server's routing facts, or `null` when they could not be read.
 * paused : boolean
 *     Whether new entries are paused.
 * clock : ServerClock or null
 *     The server's clock at the facts' snapshot (`null` with no facts).
 * mode : Mode or null
 *     The facts envelope's mode: with no live code record, ASSISTED and LIVE still require one.
 *
 * Returns
 * -------
 * ResumeGate
 *     The decision.
 */
export function resumeGate(
  facts: RoutingFacts | null,
  paused: boolean,
  clock: ServerClock | null,
  mode: Mode | null,
): ResumeGate {
  if (!paused) {
    return { ready: false, blocker: "Entries are not paused.", guard: undefined };
  }
  if (facts === null || clock === null) {
    return {
      ready: false,
      blocker:
        "The routing facts are unavailable, so the cooldown, review and code cannot be checked.",
      guard: undefined,
    };
  }
  const { halt } = facts;
  if (halt === null) {
    return { ready: false, blocker: "Entries are not halted.", guard: undefined };
  }
  return {
    ready: true,
    blocker: undefined,
    guard: {
      clock,
      reviewRequired: halt.reviewRequired,
      cooldown: { endsAt: cooldownEnd(halt) },
      ...codeGuardOf("resume", facts, mode),
    },
  };
}
